/**
 * E2E Account Setup Service
 * Handles keypair generation and registration during account creation or first login
 * Now device-aware: generates fresh keypair per device
 */

import { generateUserKeypair, storePrivateKeyLocally } from './e2eEncryptionService';
import { registerUserPublicKey } from './keyExchangeService';

/**
 * Get device ID from session/IndexedDB
 * This is created by SecureSessionManager during auth initialization
 */
const getDeviceId = async () => {
  return new Promise((resolve) => {
    try {
      // First check sessionStorage (if available during this session)
      const sessionDeviceId = sessionStorage.getItem('deviceId');
      if (sessionDeviceId) {
        resolve(sessionDeviceId);
        return;
      }
      
      // Otherwise get from IndexedDB (SecureSessionManager stores it there)
      const request = indexedDB.open('CrashCabin_Secure', 1);
      
      request.onerror = () => {
        // If IndexedDB fails, generate a temporary one for this session
        const tempDeviceId = 'device_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('deviceId', tempDeviceId);
        resolve(tempDeviceId);
      };
      
      request.onsuccess = () => {
        const db = request.result;
        
        // Try to get from session store
        try {
          const tx = db.transaction(['session'], 'readonly');
          const store = tx.objectStore('session');
          const getRequest = store.get('deviceId');
          
          getRequest.onsuccess = () => {
            const deviceId = getRequest.result;
            if (deviceId) {
              db.close();
              sessionStorage.setItem('deviceId', deviceId);
              resolve(deviceId);
            } else {
              db.close();
              const tempDeviceId = 'device_' + Math.random().toString(36).substr(2, 9);
              sessionStorage.setItem('deviceId', tempDeviceId);
              resolve(tempDeviceId);
            }
          };
          
          getRequest.onerror = () => {
            db.close();
            const tempDeviceId = 'device_' + Math.random().toString(36).substr(2, 9);
            sessionStorage.setItem('deviceId', tempDeviceId);
            resolve(tempDeviceId);
          };
        } catch (e) {
          db.close();
          const tempDeviceId = 'device_' + Math.random().toString(36).substr(2, 9);
          sessionStorage.setItem('deviceId', tempDeviceId);
          resolve(tempDeviceId);
        }
      };
    } catch (error) {
      const tempDeviceId = 'device_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('deviceId', tempDeviceId);
      resolve(tempDeviceId);
    }
  });
};

/**
 * Initialize E2E encryption for user (called once during signup or first login)
 * NOW DEVICE-SPECIFIC: Generates fresh keypair per device
 * 
 * Flow:
 * 1. Gets device ID (unique per browser/device)
 * 2. Checks if device-specific keypair exists
 * 3. If not, generates fresh keypair for this device
 * 4. Stores device-specific private key locally (never sent to server)
 * 5. Stores device info with public key in Firestore
 */
export const initializeUserE2E = async (userId) => {
  try {
    // Get the current device ID
    const deviceId = await getDeviceId();
    
    // Step 1: Check if we already have a device-specific private key
    const existingPrivateKey = await getStoredPrivateKey(userId, deviceId);

    if (existingPrivateKey) {
      // Check if public key is also registered in Firestore
      const { getUserPublicKey } = await import('./keyExchangeService');
      const existingPublicKey = await getUserPublicKey(userId);

      if (existingPublicKey) {
        // Verify integrity: Do these keys actually work together?
        const { verifyKeypairIntegrity } = await import('./e2eEncryptionService');
        const isValid = await verifyKeypairIntegrity(userId, existingPublicKey, existingPrivateKey);

        if (isValid) {
          return {
            status: 'restored',
            publicKey: existingPublicKey,
            deviceId
          };
        }
        // Fall through to regeneration below if keypair is invalid
      }
      // Fall through to regeneration below if public key not found
    }

    // ✅ IMPORTANT: Generate FRESH keypair for this device
    // Each device gets its own keypair, preventing cross-device key conflicts

    // Step 1: Generate NEW keypair locally (browser only)
    const keypair = await generateUserKeypair();

    // Step 2: Store device-specific private key in IndexedDB
    await storePrivateKeyLocally(userId, keypair.secretKey, deviceId);

    // Step 3: Upload public key to Firestore with device context
    await registerUserPublicKey(userId, keypair.publicKey, deviceId);

    return {
      status: 'initialized',
      publicKey: keypair.publicKey,
      deviceId,
      // Never return private key to caller
    };
  } catch (error) {
    throw new Error('Failed to initialize E2E encryption: ' + error.message);
  }
};

/**
 * Get stored private key from IndexedDB with device context
 */
export const getStoredPrivateKey = async (userId, deviceId = null) => {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open('e2eEncryption', 2);

      request.onerror = () => resolve(null);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('keys')) {
          db.createObjectStore('keys');
        }
      };

      request.onsuccess = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains('keys')) {
          db.close();
          resolve(null);
          return;
        }

        const transaction = db.transaction(['keys'], 'readonly');
        const store = transaction.objectStore('keys');
        
        // If deviceId provided, look for device-specific key first
        if (deviceId) {
          const deviceSpecificKeyName = `${userId}_${deviceId}_secretKey`;
          const getRequest = store.get(deviceSpecificKeyName);
          
          getRequest.onsuccess = () => {
            if (getRequest.result) {
              db.close();
              resolve(getRequest.result);
              return;
            }
            
            // Device-specific not found, try legacy for backward compatibility
            const legacyRequest = store.get(`${userId}_secretKey`);
            legacyRequest.onsuccess = () => {
              db.close();
              resolve(legacyRequest.result || null);
            };
            legacyRequest.onerror = () => {
              db.close();
              resolve(null);
            };
          };
          
          getRequest.onerror = () => {
            db.close();
            resolve(null);
          };
        } else {
          // No device ID, use legacy lookup
          const getRequest = store.get(`${userId}_secretKey`);
          
          getRequest.onsuccess = () => {
            db.close();
            resolve(getRequest.result || null);
          };
          getRequest.onerror = () => {
            db.close();
            resolve(null);
          };
        }
      };
    } catch (error) {
      resolve(null);
    }
  });
};

/**
 * Verify E2E is properly initialized for user
 */
export const verifyE2ESetup = async (userId) => {
  try {
    const privateKey = await getStoredPrivateKey(userId);

    if (!privateKey) {
      return {
        initialized: false,
        hasPrivateKey: false,
      };
    }

    return {
      initialized: true,
      hasPrivateKey: true,
    };
  } catch (error) {
    return {
      initialized: false,
      error: error.message,
    };
  }
};

/**
 * Handle user logout - optionally clear sensitive data
 */
export const handleE2ELogout = async (userId) => {
  try {
    // Import here to avoid circular dependency
    const { clearPrivateKeyCache } = await import('./e2ePrivateKeyCache');
    
    // Clear in-memory private key cache for security
    clearPrivateKeyCache(userId);
    
    // IndexedDB persists for security (can be accessed again after login)
    return true;
  } catch (error) {
    // Log error but don't fail logout
    console.error('Error during E2E logout cleanup:', error);
    return false;
  }
};

/**
 * Get user's public key (for sharing with others)
 */
export const getUserPublicKeyForSharing = async (userId) => {
  try {
    const request = indexedDB.open('e2eEncryption', 2);

    return new Promise((resolve) => {
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('keys')) {
          db.createObjectStore('keys');
        }
      };

      request.onsuccess = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains('keys')) {
          db.close();
          resolve(null);
          return;
        }

        const transaction = db.transaction(['keys'], 'readonly');
        const store = transaction.objectStore('keys');
        const getRequest = store.get(`${userId}_publicKey`);

        getRequest.onsuccess = () => {
          db.close();
          resolve(getRequest.result || null);
        };
        getRequest.onerror = () => {
          db.close();
          resolve(null);
        };
      };
      request.onerror = () => resolve(null);
    });
  } catch (error) {
    return null;
  }
};

/**
 * Emergency: Regenerate keypair if private key is compromised
 * Note: User will need to re-share public key with contacts
 */
export const regenerateE2EKeys = async (userId) => {
  try {
    // Generate new keypair
    const newKeypair = await generateUserKeypair();

    // Store new private key
    await storePrivateKeyLocally(userId, newKeypair.secretKey);

    // Upload new public key
    await registerUserPublicKey(userId, newKeypair.publicKey);

    return {
      status: 'regenerated',
      publicKey: newKeypair.publicKey,
    };
  } catch (error) {
    throw new Error('Failed to regenerate E2E keys: ' + error.message);
  }
};

/**
 * Export private key for backup (DANGEROUS - only show with user consent)
 * User must explicitly export and secure backup
 */
export const exportPrivateKeyForBackup = async (userId, userPassword) => {
  try {
    // In production, verify user password before exporting
    console.warn('⚠️ Exporting private key for backup');

    const privateKey = await getStoredPrivateKey(userId);

    if (!privateKey) {
      throw new Error('No private key found');
    }

    // Return private key (caller responsible for secure storage)
    return {
      privateKey,
      instructions: `SAVE THIS CAREFULLY - If you lose this key, you cannot decrypt messages. Store in encrypted password manager.`,
    };
  } catch (error) {
    console.error('Error exporting private key:', error);
    throw error;
  }
};

const e2eAccountSetupService = {
  initializeUserE2E,
  getStoredPrivateKey,
  verifyE2ESetup,
  handleE2ELogout,
  getUserPublicKeyForSharing,
  regenerateE2EKeys,
  exportPrivateKeyForBackup,
};

export default e2eAccountSetupService;
